import { useCallback, useRef, useState } from 'react';
import {
	Button,
	ButtonBorderRadius,
	ButtonColors,
	ButtonHeight,
} from '~/components/ui/Button';
import { CloseButton } from '~/components/ui/CloseButton';
import { Divider } from '~/components/ui/Divider';
import { ScrollContainer } from '~/components/ui/ScrollContainer';
import { Text } from '~/components/ui/Text';
import { TextInput } from '~/components/ui/TextInput';
import { rage, useInputAction } from '~/rage';
import { InputAction } from '~/shared';
import {
	StyledContainer,
	StyledHead,
	StyledTextArea,
	StyledTextAreaContainer,
	StyledTextAreaStatsContainer,
} from './FeedbackLight.styled';

const FeedbackLight = () => {
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [textareaValue, setTextareaValue] = useState('');
	const [themeValue, setThemeValue] = useState('');

	const config = rage.gameConfig.configs.gameplay;
	const titleMin = config?.feedbackTitleMinLength ?? 3;
	const titleMax = config?.feedbackTitleMaxLength ?? 100;
	const messageMax = config?.feedbackMessageMaxLength ?? 1000;

	const handleClose = () => {
		rage.feedback.close();
	};

	const handleSend = useCallback(async () => {
		if (themeValue.trim().length < titleMin) {
			return;
		}

		await rage.feedback.sendFeedback(themeValue, textareaValue);
		handleClose();
	}, [themeValue, textareaValue, titleMin]);

	useInputAction(InputAction.CommonMenuClose, handleClose);

	const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		if (e.target.value.length > messageMax) {
			return;
		}

		setTextareaValue(e.target.value);
	};

	const handleTextareaStatsMouseDown = (
		e: React.MouseEvent<HTMLDivElement>,
	) => {
		e.preventDefault();
		textareaRef.current?.focus();
	};

	const isThemeValid = themeValue.trim().length >= titleMin;
	const isMessageValid = textareaValue.trim().length > 0;
	const canSubmit = isThemeValid && isMessageValid;

	const getSubmitButtonText = () => {
		if (!isThemeValid) {
			if (themeValue.trim().length > 0) {
				return `Минимум ${titleMin} символов в теме`;
			}
			return 'Введите тему сообщения выше';
		}
		if (!isMessageValid) {
			return 'Введите текст сообщения';
		}
		return 'Отправить сообщение';
	};

	const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.value.length > titleMax) {
			return;
		}

		setThemeValue(e.target.value);
	};

	return (
		<StyledContainer>
			<StyledHead>
				<Text>Окно тестирования</Text>
				<CloseButton
					height={ButtonHeight.Height20}
					color={ButtonColors.Default}
					borderRadius={ButtonBorderRadius.Radius5}
					iconSize={10}
					onClick={handleClose}
				/>
			</StyledHead>
			<Divider $direction="H" style={{ margin: '20px 0', opacity: 0.1 }} />
			<TextInput
				placeholder="Введите название системы / темы сообщения"
				height={50}
				fontSize={14}
				padding="0 20px"
				value={themeValue}
				active={themeValue.length > 0}
				onChange={handleTitleChange}
				style={{ borderRadius: 5 }}
			/>
			<StyledTextAreaContainer>
				<ScrollContainer
					height={163}
					width={330}
					style={{ position: 'relative' }}
					scrollbarRight={-10}
					scrollableRef={textareaRef}
					scrollbarThumbColor="rgba(255, 255, 255, 0.25)"
					scrollbarThumbHoverColor="#ffffff"
				>
					<StyledTextArea
						ref={textareaRef}
						placeholder="Введите ваше сообщение"
						value={textareaValue}
						onChange={handleTextareaChange}
					/>
				</ScrollContainer>
				<StyledTextAreaStatsContainer
					onMouseDown={handleTextareaStatsMouseDown}
				>
					<Text style={{ fontSize: 12, color: '#FFFFFF', opacity: 0.25 }}>
						Максимальное кол-во символов
					</Text>
					<Text style={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.25)' }}>
						<span style={{ color: '#FFFFFF', fontWeight: 500 }}>
							{textareaValue.length}
						</span>{' '}
						/ {messageMax}
					</Text>
				</StyledTextAreaStatsContainer>
			</StyledTextAreaContainer>
			<Button
				color={ButtonColors.Select2}
				height={ButtonHeight.Height50}
				borderRadius={ButtonBorderRadius.Radius5}
				disabled={!canSubmit}
				style={{ marginTop: 20, fontSize: 16 }}
				onClick={handleSend}
			>
				<Text>{getSubmitButtonText()}</Text>
			</Button>
		</StyledContainer>
	);
};

export { FeedbackLight };
